// ─── the app shell ───────────────────────────────────────────────────────────
// Header, face stage, transcript, dock, alerts. It owns no session state: `main.ts`
// tells it what to show, and it reports what was clicked. The status pill and the
// face are driven from the *same* `FaceMode`, so what the face is doing and what the
// UI claims can never disagree.

import type { TurnTaking } from '@shared/protocol.ts'
import type { FaceMode } from '@voicebox/face'
import { BRAND } from '../brand.ts'
import { CSS } from './styles.ts'
import { TranscriptView } from './transcript.ts'

/** Socket lifecycle, orthogonal to what the assistant is doing. */
export type Connection = 'idle' | 'opening' | 'live' | 'error'

export interface ShellHandlers {
  /** Main button: start a session, or hang up. */
  onTalk(): void
  /** Interrupt — the manual barge-in. */
  onStop(): void
  /** Push-to-talk key or button state. */
  onHold(held: boolean): void
  onSay(text: string): void
  onTurnTaking(mode: TurnTaking): void
  onOpenSettings(): void
  /** "I want my own avatar" — from the first-run guide or a dropped file. */
  onWantAvatar(): void
  /** A `.vrm` dropped anywhere on the stage. */
  onDropModel(file: File): void
}

/** What the browser says about the microphone, before we ever ask for a stream. */
export type MicPermission = 'granted' | 'prompt' | 'denied' | 'unknown'

const MIC_STATE: Record<MicPermission, string> = {
  granted: 'allowed',
  prompt: 'your browser will ask when you press Start',
  denied: 'blocked — allow it in the address bar to talk',
  unknown: 'asked for when you press Start',
}

function led(): HTMLElement {
  const node = document.createElement('span')
  node.className = 'led'
  return node
}

function text(value: string): Text {
  return document.createTextNode(value)
}

const MODE_LABEL: Record<FaceMode, string> = {
  idle: 'idle',
  listening: 'listening',
  thinking: 'thinking',
  speaking: 'speaking',
}

const CONNECTION_LABEL: Record<Connection, string> = {
  idle: 'not connected',
  opening: 'connecting',
  live: 'live',
  error: 'failed',
}

export class Shell {
  readonly root: HTMLElement
  readonly faceStage: HTMLElement
  readonly transcript: TranscriptView
  private readonly pill: HTMLElement
  private readonly pillMode: HTMLElement
  private readonly pillConn: HTMLElement
  private readonly sessionChip: HTMLButtonElement
  private readonly talk: HTMLButtonElement
  private readonly push: HTMLButtonElement
  private readonly stop: HTMLButtonElement
  private readonly meter: HTMLElement
  private readonly say: HTMLInputElement
  private readonly hint: HTMLElement
  private readonly seg: HTMLElement
  private readonly alerts: HTMLElement
  private micState: HTMLElement | null = null
  private connection: Connection = 'idle'
  private mode: FaceMode = 'idle'
  private gate: TurnTaking = 'vad'

  constructor(
    host: HTMLElement,
    private readonly handlers: ShellHandlers,
  ) {
    document.head.append(Object.assign(document.createElement('style'), { textContent: CSS }))

    host.innerHTML = `
      <div id="vb">
        <header>
          <div class="wordmark">${BRAND.wordmark}</div>
          <div class="tagline grow">${BRAND.tagline}</div>
          <button class="chip" id="session" title="Switch provider"><span class="what">no session</span></button>
          <button class="icon-btn" id="gear" title="Settings" aria-label="Settings">⚙</button>
        </header>
        <main>
          <section id="stage" class="glass">
            <div id="face"></div>
            <div id="pill" class="glass" data-conn="idle">
              <span class="dot"></span><span id="pill-mode">idle</span><span class="what" id="pill-conn">not connected</span>
            </div>
          </section>
          <section id="log" class="glass" aria-live="polite"></section>
        </main>
        <div id="alerts"></div>
        <footer class="glass">
          <button id="talk"><span class="led"></span>Start talking</button>
          <button id="push" class="ghost" hidden>Hold to talk</button>
          <button id="stop" class="ghost" hidden>Interrupt</button>
          <div class="seg" id="seg" role="group" aria-label="Turn taking">
            <button data-turn="vad" aria-pressed="true">Voice</button>
            <button data-turn="ptt" aria-pressed="false">Push</button>
          </div>
          <!-- The bar and the sentence that says what it is measuring, together: on its
               own the bar reads as a stray line, and it is meaningless with no mic open. -->
          <div class="level">
            <div id="meter" role="meter" aria-label="Microphone level" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0"><i></i></div>
            <div class="hint" id="hint">Not connected</div>
          </div>
          <input id="say" placeholder="…or type a turn and press enter" autocomplete="off" />
        </footer>
      </div>`

    this.root = host.querySelector('#vb')!
    this.faceStage = host.querySelector('#face')!
    this.transcript = new TranscriptView(host.querySelector('#log')!, this.buildCoach())
    this.pill = host.querySelector('#pill')!
    this.pillMode = host.querySelector('#pill-mode')!
    this.pillConn = host.querySelector('#pill-conn')!
    this.sessionChip = host.querySelector('#session')!
    this.talk = host.querySelector('#talk')!
    this.push = host.querySelector('#push')!
    this.stop = host.querySelector('#stop')!
    this.meter = host.querySelector('#meter > i')!
    this.say = host.querySelector('#say')!
    this.hint = host.querySelector('#hint')!
    this.seg = host.querySelector('#seg')!
    this.alerts = host.querySelector('#alerts')!

    this.talk.onclick = () => handlers.onTalk()
    this.stop.onclick = () => handlers.onStop()
    host.querySelector<HTMLElement>('#gear')!.onclick = () => handlers.onOpenSettings()
    // The provider is the thing most worth changing, so the chip that names it is
    // also the way to change it.
    this.sessionChip.onclick = () => handlers.onOpenSettings()
    this.seg.onclick = (e) => {
      const turn = (e.target as HTMLElement).dataset.turn as TurnTaking | undefined
      if (turn) handlers.onTurnTaking(turn)
    }
    this.say.onkeydown = (e) => {
      if (e.key !== 'Enter' || !this.say.value.trim()) return
      handlers.onSay(this.say.value.trim())
      this.say.value = ''
    }

    this.wirePushToTalk()
    this.wireModelDrop(host.querySelector('#stage')!)
    this.fitToVisualViewport()
    this.render()
  }

  /**
   * A phone's on-screen keyboard shrinks the *visual* viewport but not the layout one,
   * so a shell pinned to `inset: 0` would keep the dock — the composer you are typing
   * into — underneath the keyboard. Following the visual viewport keeps every control
   * on screen. Desktop reports the two as identical, so nothing moves there.
   */
  private fitToVisualViewport(): void {
    const view = window.visualViewport
    if (!view) return
    const fit = () => {
      this.root.style.top = `${view.offsetTop}px`
      this.root.style.height = `${view.height}px`
    }
    view.addEventListener('resize', fit)
    view.addEventListener('scroll', fit)
    fit()
  }

  /**
   * The empty transcript doubles as the first-run guide: three steps, in the order
   * they happen, with the microphone's real state in step one. It disappears by
   * itself as soon as anything is said.
   */
  private buildCoach(): HTMLElement {
    const card = document.createElement('div')
    card.className = 'coach'
    card.innerHTML = `
      <div class="coach-title">Three steps</div>
      <ol>
        <li><b>Allow the microphone</b><span class="mic-state" id="mic-state">${MIC_STATE.unknown}</span></li>
        <li><b>Press ${'\u201C'}Start talking${'\u201D'}</b><span>then just speak — it decides when you have finished</span></li>
        <li><b>Bring your own avatar</b><span>drop a <code>.vrm</code> or <code>.mavatar</code> on the face, or pick one</span></li>
      </ol>
      <button class="ghost" id="want-avatar">Load a .vrm avatar</button>`
    card.querySelector<HTMLElement>('#want-avatar')!.onclick = () => this.handlers.onWantAvatar()
    this.micState = card.querySelector('#mic-state')!
    return card
  }

  /**
   * Drag a `.vrm` onto the face and it is worn. The licence for an avatar is never
   * ours to bundle, so the only way in is the user supplying one — that path should
   * be the most obvious thing on screen, not a menu item.
   */
  private wireModelDrop(stage: HTMLElement): void {
    const over = (on: boolean) => stage.setAttribute('data-drop', String(on))
    stage.addEventListener('dragover', (e) => {
      e.preventDefault()
      over(true)
    })
    stage.addEventListener('dragleave', () => over(false))
    stage.addEventListener('drop', (e) => {
      e.preventDefault()
      over(false)
      const file = e.dataTransfer?.files?.[0]
      if (file) this.handlers.onDropModel(file)
    })
  }

  setMicPermission(state: MicPermission): void {
    if (this.micState) this.micState.textContent = MIC_STATE[state]
    this.micState?.setAttribute('data-state', state)
  }

  /**
   * Space is the push-to-talk key, but only when the user is not typing — a
   * space-bar that swallows itself mid-sentence is worse than no shortcut.
   */
  private wirePushToTalk(): void {
    const typing = () => document.activeElement === this.say
    addEventListener('keydown', (e) => {
      if (e.code !== 'Space' || e.repeat || typing() || this.gate !== 'ptt') return
      e.preventDefault()
      this.setHeld(true)
    })
    addEventListener('keyup', (e) => {
      if (e.code !== 'Space' || typing()) return
      this.setHeld(false)
    })
    this.push.onpointerdown = () => this.setHeld(true)
    this.push.onpointerup = () => this.setHeld(false)
    this.push.onpointerleave = () => this.setHeld(false)
    // A window that loses focus mid-hold must not leave the mic open.
    addEventListener('blur', () => this.setHeld(false))
  }

  private held = false
  private setHeld(held: boolean): void {
    if (held === this.held) return
    this.held = held
    this.push.setAttribute('aria-pressed', String(held))
    this.push.textContent = held ? 'Listening…' : 'Hold to talk'
    this.handlers.onHold(held)
  }

  setConnection(connection: Connection): void {
    this.connection = connection
    this.render()
  }

  setMode(mode: FaceMode): void {
    if (mode === this.mode) return
    this.mode = mode
    this.render()
  }

  setSession(text: string, placement?: string): void {
    this.sessionChip.className = `chip placement-${placement ?? 'none'}`
    this.sessionChip.innerHTML = ''
    this.sessionChip.append(Object.assign(document.createElement('span'), { textContent: text }))
  }

  setTurnTaking(gate: TurnTaking): void {
    this.gate = gate
    for (const button of this.seg.querySelectorAll('button')) {
      button.setAttribute('aria-pressed', String(button.dataset.turn === gate))
    }
    this.render()
  }

  setLevel(level: number): void {
    const percent = Math.round(Math.min(1, level) * 100)
    this.meter.style.width = `${percent}%`
    this.meter.parentElement?.setAttribute('aria-valuenow', String(percent))
  }

  setBusy(busy: boolean): void {
    this.talk.disabled = busy
  }

  /** A failure the user must see. Errors stack; the newest is on top. */
  alert(head: string, detail?: string | string[]): void {
    this.banner('alert', head, detail)
  }

  /** Something worth knowing that is not a failure (a fallback, a swap). */
  notice(head: string, detail?: string | string[]): void {
    this.banner('alert notice', head, detail)
  }

  private banner(className: string, head: string, detail?: string | string[]): void {
    const node = document.createElement('div')
    node.className = `${className} glass`
    const body = document.createElement('div')
    body.className = 'body'
    body.append(Object.assign(document.createElement('div'), { className: 'head', textContent: head }))
    const lines = Array.isArray(detail) ? detail : detail ? [detail] : []
    if (lines.length) {
      body.append(Object.assign(document.createElement('div'), { className: 'detail', textContent: lines.join(' · ') }))
    }
    const close = Object.assign(document.createElement('button'), { className: 'x', textContent: '✕', title: 'Dismiss' })
    close.onclick = () => node.remove()
    node.append(body, close)
    this.alerts.prepend(node)
    // Cap the stack: a provider failing in a loop must not bury the app.
    while (this.alerts.children.length > 4) this.alerts.lastElementChild!.remove()
  }

  private render(): void {
    const live = this.connection === 'live'
    // On the root as well as the pill: the dock hides the microphone meter when there
    // is no microphone, which is a layout decision the stylesheet can make on its own.
    this.root.dataset.conn = this.connection
    this.pill.dataset.conn = this.connection
    this.pillMode.textContent = MODE_LABEL[this.mode]
    this.pillConn.textContent = CONNECTION_LABEL[this.connection]
    this.talk.replaceChildren(led(), text(live ? 'Hang up' : 'Start talking'))
    this.talk.dataset.live = String(live)
    this.push.hidden = !live || this.gate !== 'ptt'
    this.stop.hidden = !live
    this.hint.textContent = !live
      ? this.connection === 'opening'
        ? 'Opening a session…'
        : 'Not connected'
      : this.gate === 'ptt'
        ? // A phone has the Hold-to-talk button and no space bar, so the hint names both.
          'Hold the button, or space'
        : 'Just talk — it is listening'
  }
}
